import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "template", "hiddenFields"]
  static values = {
    resource: String,
    action: String,
    permissionId: Number,
    index: Number
  }

  // New methods for checkbox-based permission handling
  connect() {
    // Find the checkbox within this controller's element
    const checkbox = this.checkbox
    if (checkbox) {
      if (checkbox.checked) {
        this.ensureHiddenFields()
      } else if (this.hasPermissionIdValue && this.permissionIdValue) {
        // For existing permissions that are unchecked on page load, set _destroy
        // But only if hidden fields already exist (they should from server-side rendering)
        if (this.hiddenFieldsContainer && this.hiddenFieldsContainer.querySelector('input[name*="[id]"]')) {
          this.setDestroyFlag()
        }
      }
    }
  }

  toggle() {
    if (!this.hasHiddenFieldsTarget) return

    const checkbox = this.checkbox
    if (!checkbox) return

    if (checkbox.checked) {
      this.ensureHiddenFields()
      // Remove _destroy flag if it exists
      this.removeDestroyFlag()
    } else {
      if (this.hasPermissionIdValue && this.permissionIdValue) {
        // Existing permission: ensure fields exist, then mark for destruction
        this.ensureHiddenFields() // Make sure id, resource, action fields exist
        this.setDestroyFlag()
      } else {
        // New permission: remove hidden fields
        this.removeHiddenFields()
      }
    }
  }

  get checkbox() {
    // Find the checkbox input within this controller's element
    return this.element.querySelector('input[type="checkbox"]')
  }

  get hiddenFieldsContainer() {
    return this.hasHiddenFieldsTarget ? this.hiddenFieldsTarget : null
  }

  ensureHiddenFields() {
    if (!this.hiddenFieldsContainer) return

    // Check if we already have the required fields
    const hasResource = this.hiddenFieldsContainer.querySelector('input[name*="[resource]"]')
    const hasAction = this.hiddenFieldsContainer.querySelector('input[name*="[action]"]')

    if (hasResource && hasAction) {
      // Fields already exist, just ensure _destroy is removed
      this.removeDestroyFlag()
      return
    }

    const index = this.getOrCreateIndex()

    // Create resource field
    if (!hasResource) {
      const resourceField = this.createHiddenField(
        `role[permissions_attributes][${index}][resource]`,
        this.resourceValue
      )
      this.hiddenFieldsContainer.appendChild(resourceField)
    }

    // Create action field
    if (!hasAction) {
      const actionField = this.createHiddenField(
        `role[permissions_attributes][${index}][action]`,
        this.actionValue
      )
      this.hiddenFieldsContainer.appendChild(actionField)
    }

    // Add ID field only for existing permissions
    if (this.hasPermissionIdValue && this.permissionIdValue) {
      const hasId = this.hiddenFieldsContainer.querySelector('input[name*="[id]"]')
      if (!hasId) {
        const idField = this.createHiddenField(
          `role[permissions_attributes][${index}][id]`,
          this.permissionIdValue
        )
        this.hiddenFieldsContainer.insertBefore(idField, this.hiddenFieldsContainer.firstChild)
      }
    }
  }

  setDestroyFlag() {
    if (!this.hiddenFieldsContainer) return

    // Ensure we have the required fields before adding _destroy
    this.ensureHiddenFields()

    const index = this.getOrCreateIndex()
    if (!index) return

    // Remove existing _destroy field if any
    this.removeDestroyFlag()

    // Add _destroy field
    const destroyField = this.createHiddenField(
      `role[permissions_attributes][${index}][_destroy]`,
      "1"
    )
    this.hiddenFieldsContainer.appendChild(destroyField)
  }

  removeDestroyFlag() {
    if (!this.hiddenFieldsContainer) return

    const destroyField = this.hiddenFieldsContainer.querySelector(
      'input[name*="[_destroy]"]'
    )
    if (destroyField) {
      destroyField.remove()
    }
  }

  removeHiddenFields() {
    if (this.hiddenFieldsContainer) {
      this.hiddenFieldsContainer.innerHTML = ""
    }
  }

  getOrCreateIndex() {
    if (!this.hiddenFieldsContainer) return this.generateIndex()

    // Try to get index from existing fields first
    const resourceField = this.hiddenFieldsContainer.querySelector(
      'input[name*="[resource]"]'
    )
    if (resourceField) {
      // Match numeric indices (0, 1, 2, etc.)
      const match = resourceField.name.match(/\[(\d+)\]\[resource\]/)
      if (match) {
        return match[1]
      }
    }

    // Use provided index if available
    if (this.hasIndexValue && this.indexValue !== null && this.indexValue !== undefined) {
      return this.indexValue
    }

    // Generate a unique numeric index for new permissions
    return this.generateIndex()
  }

  createHiddenField(name, value) {
    const field = document.createElement("input")
    field.type = "hidden"
    field.name = name
    field.value = value
    return field
  }

  generateIndex() {
    // Find the highest existing index in the form to avoid conflicts
    const form = this.element.closest('form')
    if (!form) {
      // Fallback: use timestamp-based number if form not found
      return Date.now()
    }

    // Find all permission attribute indices in the form
    const allInputs = form.querySelectorAll('input[name*="[permissions_attributes]"]')
    let maxIndex = -1

    allInputs.forEach(input => {
      const match = input.name.match(/\[permissions_attributes\]\[(\d+)\]/)
      if (match) {
        const index = parseInt(match[1], 10)
        if (index > maxIndex) {
          maxIndex = index
        }
      }
    })

    // Return the next available index (maxIndex + 1)
    // If no existing indices found, start from a high number to avoid conflicts
    return maxIndex >= 0 ? maxIndex + 1 : Date.now()
  }
}
